/**
 * T-249 (Sascha Hahn, 08.10.2026): „warum werden mir 34 ungelesene im Postfach
 * angezeigt und 43 bei den Benachrichtigungen". Die Glocke, das Badge an
 * „Schriftverkehr" und die Dashboard-Karte zählten die Ungelesenen ALLER
 * lesbaren Postfächer zusammen — cbb@ und schaden@ inklusive. Der Posteingang
 * zeigt dagegen nur das eigene. PO: „muss gleich sein" und „einstellbar sein".
 *
 * Deshalb eine Nutzer-Einstellung, welche Postfächer zählen. STANDARD (`null`)
 * ist nur das eigene Postfach — dann steht in der Glocke dieselbe Zahl wie am
 * eigenen Posteingang. Reine, DOM-freie Logik (unter Vitest ohne Rendering
 * prüfbar); den gemeinsamen Stand hält `useGlockenPostfaecher`.
 */

/** Scope in der `UserPreference`-Ablage (pro Person, serverseitig gespeichert). */
export const GLOCKE_POSTFAECHER_SCOPE = "mail.glocke.postfaecher";

/**
 * `null` = Standard (nur das eigene Postfach). Sonst die gewählten Adressen,
 * klein geschrieben — eine leere Liste heißt bewusst „keine Mails in der Glocke".
 */
export type GlockenWahl = string[] | null;

const klein = (adresse: string | null | undefined): string => (adresse ?? "").trim().toLowerCase();

/** Alles außer einer Liste fällt auf den Standard zurück; die Liste wird bereinigt. */
export function normalisiereGlockenWahl(roh: unknown): GlockenWahl {
    if (!Array.isArray(roh)) return null;
    const out: string[] = [];
    for (const eintrag of roh) {
        if (typeof eintrag !== "string") continue;
        const adresse = klein(eintrag);
        if (!adresse.includes("@") || out.includes(adresse)) continue;
        out.push(adresse);
    }
    return out;
}

/** Zählt dieses Postfach in der Glocke mit? */
export function zaehltInGlocke(
    postfach: string,
    wahl: GlockenWahl,
    eigenesPostfach: string | null | undefined,
): boolean {
    const adresse = klein(postfach);
    if (wahl === null) return !!adresse && adresse === klein(eigenesPostfach);
    return wahl.includes(adresse);
}

/**
 * Die Zahl für Glocke, „Schriftverkehr"-Badge und Dashboard-Karte: Summe der
 * Posteingang-Ungelesenen (`unreadByMailbox`, dieselbe Quelle wie die Zahl am
 * Posteingang) über die Postfächer, die zählen.
 */
export function glockenZahl(
    unreadByMailbox: Readonly<Record<string, number>>,
    wahl: GlockenWahl,
    eigenesPostfach: string | null | undefined,
): number {
    let summe = 0;
    for (const [postfach, anzahl] of Object.entries(unreadByMailbox)) {
        if (zaehltInGlocke(postfach, wahl, eigenesPostfach)) summe += anzahl;
    }
    return summe;
}
